import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast, Bounce, ToastContainer } from "react-toastify";
import html2canvas from "html2canvas";
import InputBox from "./reusable/InputBox";
import { submitFormData } from "../services/formService";
import "react-toastify/dist/ReactToastify.css";

const TRANSACTION_TYPES = {
  MONEY_GIVEN: "Money Given",
  MONEY_RECEIVED: "Money Received",
};

function DefaultEntry({ userId, userSelectedOption, role, Name }) {
  const [selectedOption, setSelectedOption] = useState("Feed");
  const [date, setDate] = useState("");
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [moneyAmount, setMoneyAmount] = useState("");
  const [showUserAlert, setShowUserAlert] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const receiptRef = useRef(null);

  const isMoneyTransaction = useMemo(
    () => Object.values(TRANSACTION_TYPES).includes(selectedOption),
    [selectedOption]
  );

  const totalAmount = useMemo(() => {
    if (isMoneyTransaction) return Number(moneyAmount || 0).toFixed(2);
    return (Number(quantity || 0) * Number(price || 0)).toFixed(2);
  }, [quantity, price, moneyAmount, isMoneyTransaction]);

  useEffect(() => {
    if (userSelectedOption) setSelectedOption(userSelectedOption);
    setDate(new Date().toISOString().split("T")[0]);
  }, [userSelectedOption]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId && role === "admin") {
      setShowUserAlert(true);
      return;
    }

    const formData = {
      userId,
      date,
      selectedOption,
      quantity,
      price,
      moneyAmount,
    };

    const endpoint =
      selectedOption === TRANSACTION_TYPES.MONEY_GIVEN
        ? "/addMoney"
        : selectedOption === TRANSACTION_TYPES.MONEY_RECEIVED
        ? "/receiveMoney"
        : "/items";

    try {
      await toast.promise(submitFormData(endpoint, formData), {
        pending: "Submitting...",
        success: "Entry added successfully",
        error: "Submission failed",
      });

      if (role === "admin") {
        setReceiptData({
          ...formData,
          total: totalAmount,
          time: new Date().toLocaleTimeString(),
          userName: Name,
        });
        setShowReceiptPopup(true);
      }

      setQuantity("");
      setPrice("");
      setMoneyAmount("");
    } catch (err) {
      console.error(err);
    }
  };

  const generateReceiptImage = async () => {
    const canvas = await html2canvas(receiptRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
    });
    return canvas;
  };

  const printReceipt = async () => {
    const canvas = await generateReceiptImage();
    const win = window.open("", "_blank");
    win.document.write(`<img src="${canvas.toDataURL()}" />`);
    win.print();
  };

  const shareReceipt = async () => {
    setIsProcessing(true);
    try {
      const canvas = await generateReceiptImage();
      const blob = await new Promise((r) => canvas.toBlob(r));
      const file = new File([blob], "receipt.png", { type: "image/png" });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Receipt" });
      } else {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "receipt.png";
        link.click();
      }
    } catch {
      toast.error("Sharing failed");
    }
    setIsProcessing(false);
  };

  return (
    <div className="w-full px-2 md:px-4">
      {/* USER ALERT */}
      {showUserAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-6 w-80 text-center shadow-xl">
            <h3 className="text-lg font-bold text-red-600 mb-2">
              Select Customer
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Please select a customer before submitting.
            </p>
            <button
              onClick={() => setShowUserAlert(false)}
              className="bg-gray-100 px-4 py-2 rounded-lg font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* RECEIPT ACTIONS */}
      {showReceiptPopup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm space-y-3">
            <h2 className="text-xl font-extrabold text-center">
              Receipt Options
            </h2>
            <button
              onClick={printReceipt}
              className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold"
            >
              Print Receipt
            </button>
            <button
              onClick={shareReceipt}
              disabled={isProcessing}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold disabled:opacity-50"
            >
              {isProcessing ? "Processing..." : "Share Receipt"}
            </button>
            <button
              onClick={() => setShowReceiptPopup(false)}
              className="w-full py-2 text-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* HIDDEN RECEIPT */}
      <div className="absolute left-[-9999px]">
        <div
          ref={receiptRef}
          className="w-[350px] p-6 text-sm bg-white"
          style={{ fontFamily: "Courier, monospace" }}
        >
          <h2 className="text-center font-bold border-b border-dashed pb-2">
            DAIRY RECEIPT
          </h2>
          <p>Date: {receiptData?.date}</p>
          <p>Customer: {Name}</p>
          <p>Type: {receiptData?.selectedOption}</p>
          <hr className="border-dashed my-2" />
          <div className="flex justify-between font-bold text-lg">
            <span>TOTAL</span>
            <span>₹{receiptData?.total}</span>
          </div>
          <p className="text-center mt-3">Thank you 🙏</p>
        </div>
      </div>

      {/* FORM CARD */}
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <InputBox type="date" label="Date" value={date} onValueChange={(e) => setDate(e.target.value)} />

          <div>
            <label className="text-sm font-medium">Transaction Type</label>
            <div className="mt-1 px-4 py-2 rounded-xl bg-indigo-50 text-indigo-700 font-bold inline-block">
              {selectedOption}
            </div>
          </div>

          {isMoneyTransaction ? (
            <InputBox
              type="number"
              label="Amount (₹)"
              value={moneyAmount}
              onValueChange={(e) => setMoneyAmount(e.target.value)}
              required
            />
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <InputBox type="number" label="Quantity" value={quantity} onValueChange={(e) => setQuantity(e.target.value)} required />
              <InputBox type="number" label="Price" value={price} onValueChange={(e) => setPrice(e.target.value)} required />
            </div>
          )}

          {totalAmount > 0 && (
            <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 border border-indigo-200 rounded-xl p-4 flex justify-between items-center">
              <span className="font-semibold">Grand Total</span>
              <span className="text-2xl font-extrabold text-indigo-700">
                ₹{totalAmount}
              </span>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700 transition text-white py-3 rounded-xl font-bold shadow-md"
          >
            Submit Entry
          </button>
        </form>
      </div>

      <ToastContainer position="top-center" transition={Bounce} />
    </div>
  );
}

export default DefaultEntry;
